import React, { useEffect, useMemo, useState } from 'react';
import { getPracticeOverviewProviders } from 'api/group';
import { TPracticeOverviewProviderRowVo, TPracticeOverviewRange } from '../../types';
import RangeDropdown from '../RangeDropdown';
import s from './s.module.less';

type TabKey = 'best' | 'attention';
type SortCol = 'payoutCents' | 'visits' | 'avgRating' | 'cancelRatePct' | 'revenuePerVisitCents';
type SortDir = 'asc' | 'desc';

const SORTABLE_COLUMNS: { key: SortCol; label: string }[] = [
    { key: 'payoutCents', label: 'Revenue' },
    { key: 'visits', label: 'Visits' },
    { key: 'avgRating', label: 'Avg. Rating' },
    { key: 'cancelRatePct', label: 'Cancel rate' },
    { key: 'revenuePerVisitCents', label: 'Rev/visit' },
];

const COL_INDEX_OFFSET = 1;

const DEFAULT_RANGE: TPracticeOverviewRange = 'LAST_30D';

const ProviderPerformance: React.FC = () => {
    const [range, setRange] = useState<TPracticeOverviewRange>(DEFAULT_RANGE);
    const [bestProviders, setBestProviders] = useState<TPracticeOverviewProviderRowVo[]>([]);
    const [attentionProviders, setAttentionProviders] = useState<TPracticeOverviewProviderRowVo[]>([]);
    const [activeTab, setActiveTab] = useState<TabKey>('best');
    const [sortCol, setSortCol] = useState<SortCol>('payoutCents');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    useEffect(() => {
        Promise.all([
            getPracticeOverviewProviders({ range, bucket: 'BEST_PERFORMING' }),
            getPracticeOverviewProviders({ range, bucket: 'NEEDS_ATTENTION' }),
        ]).then(([bestRes, attRes]) => {
            if (!bestRes.error) setBestProviders(bestRes?.data?.data ?? []);
            if (!attRes.error) setAttentionProviders(attRes?.data?.data ?? []);
        });
    }, [range]);

    const rawData = activeTab === 'best' ? bestProviders : attentionProviders;

    const data = useMemo(() => {
        const sorted = [...rawData].sort((a, b) => {
            const aVal = a[sortCol] ?? 0;
            const bVal = b[sortCol] ?? 0;
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [rawData, sortCol, sortDir]);

    const handleTabChange = (tab: TabKey) => {
        setActiveTab(tab);
        setSortCol('payoutCents');
        setSortDir(tab === 'best' ? 'desc' : 'asc');
    };

    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
    };

    const getInitials = (row: TPracticeOverviewProviderRowVo) => {
        const parts = (row.providerName ?? '').split(' ').filter(Boolean);
        return parts.map((p) => p[0]).join('').toUpperCase().slice(0, 2);
    };

    const sortedColIndex = SORTABLE_COLUMNS.findIndex((c) => c.key === sortCol);
    const hlClass = activeTab === 'attention' ? s.colHlRed : s.colHlGreen;

    const getRatingClass = (rating: number | null) => {
        if (rating == null) return '';
        if (rating >= 4.5) return s.perfGood;
        if (rating < 4.0) return s.perfBad;
        return '';
    };

    const getCancelClass = (pct: number) => {
        if (pct <= 5) return s.perfGood;
        if (pct >= 12) return s.perfBad;
        return '';
    };

    return (
        <div>
            <div className={s.header}>
                <h2 className={s.title}>Provider Performance</h2>
                <RangeDropdown value={range} onChange={(v) => setRange(v as TPracticeOverviewRange)} />
            </div>
            <div className={s.chips}>
                <button
                    className={`${s.chip} ${activeTab === 'best' ? s.chipActive : ''}`}
                    onClick={() => handleTabChange('best')}
                >
                    Best performing
                </button>
                <button
                    className={`${s.chip} ${activeTab === 'attention' ? s.chipActive : ''}`}
                    onClick={() => handleTabChange('attention')}
                >
                    Needs attention
                </button>
            </div>
            <div className={s.card}>
                <table className={s.table}>
                    <thead>
                        <tr>
                            <th className={sortedColIndex === -1 ? '' : undefined}>Provider</th>
                            {SORTABLE_COLUMNS.map(({ key, label }, idx) => {
                                const isSorted = sortCol === key;
                                return (
                                    <th
                                        key={key}
                                        className={[
                                            s.right,
                                            s.sortable,
                                            isSorted ? s.sorted : '',
                                            isSorted ? hlClass : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => handleSort(key)}
                                    >
                                        {label}
                                        <span className={`${s.sortArrow} ${isSorted ? s.sortArrowVisible : ''}`}>
                                            {isSorted ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => (
                            <tr key={row.providerId}>
                                <td>
                                    <div className={s.providerCell}>
                                        {row.providerAvatarUrl ? (
                                            <img className={s.avatarImg} src={row.providerAvatarUrl} alt="" />
                                        ) : (
                                            <div className={s.avatar}>{getInitials(row)}</div>
                                        )}
                                        <div>
                                            <div className={s.provName}>{row.providerName}</div>
                                            {row.providerCred && <div className={s.provSpec}>{row.providerCred}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td
                                    className={`${s.right} ${sortedColIndex === 0 ? hlClass : ''}`}
                                >
                                    ${((row.payoutCents ?? 0) / 100).toLocaleString()}
                                </td>
                                <td
                                    className={`${s.right} ${sortedColIndex === 1 ? hlClass : ''}`}
                                >
                                    {row.visits ?? 0}
                                </td>
                                <td
                                    className={`${s.right} ${getRatingClass(row.avgRating)} ${sortedColIndex === 2 ? hlClass : ''}`}
                                >
                                    {row.avgRating != null ? row.avgRating.toFixed(1) : '-'}
                                    {row.ratingCount > 0 && (
                                        <span className={s.ratingCount}> ({row.ratingCount})</span>
                                    )}
                                </td>
                                <td
                                    className={`${s.right} ${getCancelClass(row.cancelRatePct ?? 0)} ${sortedColIndex === 3 ? hlClass : ''}`}
                                >
                                    {(row.cancelRatePct ?? 0).toFixed(1)}%
                                </td>
                                <td
                                    className={`${s.right} ${sortedColIndex === 4 ? hlClass : ''}`}
                                >
                                    ${((row.revenuePerVisitCents ?? 0) / 100).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                        {data.length === 0 && (
                            <tr>
                                <td colSpan={6} className={s.empty}>
                                    No providers in this category
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProviderPerformance;
